import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router';
import { workspacesApi, pillarsApi, articlesApi, intelligenceApi } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Spinner, Alert, BackLink, StatCard } from '@rankhelm/ui';
import { workspaceTitles } from '../lib/workspace';
import type { Workspace, Pillar, Article, IntelligenceReport, IntelligenceReportVersion } from '@rankhelm/shared';

/** Intelligence report with eagerly-loaded version data from the API */
interface IntelligenceWithVersions extends IntelligenceReport {
  versions?: IntelligenceReportVersion[];
  current_version?: IntelligenceReportVersion;
}

/** Parsed intelligence report content JSON */
interface IntelContentParsed {
  status?: string;
  message?: string;
  error?: string;
  market_overview?: { summary?: string };
}

const STATUS_COLORS: Record<string, string> = {
  queued: 'bg-slate-500',
  generating: 'bg-yellow-500 animate-pulse',
  draft: 'bg-blue-500',
  in_review: 'bg-orange-500',
  revision_requested: 'bg-red-400',
  approved: 'bg-green-500',
  scheduled: 'bg-cyan-500',
  publishing: 'bg-yellow-500 animate-pulse',
  published: 'bg-emerald-500',
  publish_failed: 'bg-red-600',
};

const BADGE_COLORS: Record<string, string> = {
  draft: 'bg-yellow-500/20 text-yellow-400',
  approved: 'bg-blue-500/20 text-blue-400',
  locked: 'bg-green-500/20 text-green-400',
};

const STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  generating: 'Generating',
  draft: 'Draft',
  in_review: 'In Review',
  revision_requested: 'Revision Requested',
  approved: 'Approved',
  scheduled: 'Scheduled',
  publishing: 'Publishing',
  published: 'Published',
  publish_failed: 'Publish Failed',
};

const LIFECYCLE_STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  paused: 'Paused',
  retired: 'Retired',
};

const RESEARCH_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  approved: 'Approved',
  locked: 'Locked',
};

export function WorkspaceDashboardPage() {
  const { workspaceId } = useParams();
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [pillars, setPillars] = useState<Pillar[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [intelligence, setIntelligence] = useState<IntelligenceWithVersions | null>(null);
  const [intelContent, setIntelContent] = useState<IntelContentParsed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [isPolling, setIsPolling] = useState(false);

  const [showAddPillar, setShowAddPillar] = useState(false);
  const [pillarPrompt, setPillarPrompt] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [wsRes, pillarRes, articleRes, intelRes] = await Promise.all([
        workspacesApi.get(workspaceId!),
        pillarsApi.list(workspaceId!),
        articlesApi.list(workspaceId!),
        intelligenceApi.get(workspaceId!).catch(() => ({ data: null })),
      ]);
      setWorkspace(wsRes.data);
      setPillars(pillarRes.data);
      setArticles(articleRes.data);
      const intelData = intelRes.data as IntelligenceWithVersions | null;
      setIntelligence(intelData);

      if (intelData && intelData.versions && intelData.versions.length > 0) {
        const currentVersion = intelData.versions.find(
          (v) => v.id === intelData.current_version_id
        );
        if (currentVersion) {
          try {
            const parsed = JSON.parse(currentVersion.content_json || '{}');
            setIntelContent(parsed);
            setIsPolling(parsed.status === 'generating');
          } catch {
            setIntelContent(null);
            setIsPolling(false);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workspace');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!isPolling) return;
    const interval = setInterval(loadData, 3000);
    return () => clearInterval(interval);
  }, [isPolling, loadData]);

  const handleAddPillar = async () => {
    if (!pillarPrompt.trim()) return;
    setActionLoading('pillar');
    setError('');
    try {
      await pillarsApi.create({
        workspace_id: workspaceId!,
        name: pillarPrompt.trim().length > 80 ? pillarPrompt.trim().substring(0, 80) + '...' : pillarPrompt.trim(),
        description: pillarPrompt.trim(),
      });
      setPillarPrompt('');
      setShowAddPillar(false);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create pillar');
    } finally {
      setActionLoading('');
    }
  };

  const handleGenerateIntelligence = async () => {
    setActionLoading('intel');
    setError('');
    try {
      await intelligenceApi.generate(workspaceId!);
      setIsPolling(true);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return <Spinner message="Loading workspace..." fullPage />;
  }

  if (!workspace) return <div className="text-red-400">Workspace not found</div>;

  const statusCounts = articles.reduce((acc: Record<string, number>, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  const hasIntelligence = intelligence && intelligence.status;
  const hasBusinessContext = workspace?.business_context_json;
  const isGenerating = intelContent?.status === 'generating';
  const isError = intelContent?.status === 'error';
  const hasRealContent = intelContent && !isGenerating && !isError && intelContent.market_overview;
  const isAdmin = user?.role === 'super_admin' || user?.role === 'account_admin';

  const titles = workspaceTitles(workspace);

  return (
    <div>
      <BackLink to="/workspaces" label="Back to Workspaces" />
      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">{titles.primary}</h1>
          {titles.secondary && (
            <p className="text-slate-300 text-base mt-0.5">{titles.secondary}</p>
          )}
          <p className="text-slate-400 text-sm mt-1">
            {workspace.publish_target_type === 'wordpress' ? 'WordPress' : workspace.publish_target_type}
            {workspace.seo_plugin && ` · ${workspace.seo_plugin.charAt(0).toUpperCase() + workspace.seo_plugin.slice(1)}`}
            {workspace.nap_enabled && ' · NAP Enabled'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasBusinessContext && (
            <Link to={`/workspaces/${workspaceId}/business-context`} className="px-3 py-2 bg-navy-800 hover:bg-navy-700 text-slate-300 text-xs rounded-lg border border-navy-600 transition-colors">
              Edit Business Context
            </Link>
          )}
          <Link to={`/workspaces/${workspaceId}/settings`} className="px-4 py-2 bg-navy-800 hover:bg-navy-700 text-slate-300 text-sm rounded-lg border border-navy-600 transition-colors">
            Settings
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <StatCard label="Pillars" value={pillars.length} />
        <StatCard label="Total Articles" value={articles.length} />
        <StatCard label="Drafts" value={statusCounts.draft || 0} />
        <StatCard label="In Review" value={statusCounts.in_review || 0} />
        <StatCard label="Published" value={statusCounts.published || 0} />
      </div>

      {/* Intelligence Report Section */}
      <div className="bg-navy-900 border border-navy-700 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Intelligence Report</h2>
          <div className="flex items-center gap-2">
            {hasIntelligence && (
              <Link to={`/workspaces/${workspaceId}/intelligence`} className="px-4 py-2 bg-navy-800 hover:bg-navy-700 text-slate-300 text-sm rounded-lg border border-navy-600 transition-colors">
                View Full Report
              </Link>
            )}
            {!intelligence && !hasBusinessContext && isAdmin && (
              <Link to={`/workspaces/${workspaceId}/business-context`} className="px-4 py-2 bg-helm-500 hover:bg-helm-400 text-white text-sm rounded-lg transition-colors">
                Complete Business Context
              </Link>
            )}
            {!intelligence && hasBusinessContext && isAdmin && (
              <button onClick={handleGenerateIntelligence} disabled={actionLoading === 'intel'} className="px-4 py-2 bg-helm-500 hover:bg-helm-400 text-white text-sm rounded-lg transition-colors disabled:opacity-50">
                {actionLoading === 'intel' ? 'Starting...' : 'Generate Report'}
              </button>
            )}
          </div>
        </div>
        {intelligence ? (
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${BADGE_COLORS[intelligence.status] || 'bg-slate-500/20 text-slate-400'}`}>
                {STATUS_LABELS[intelligence.status] || intelligence.status}
              </span>
              <span className="text-xs text-slate-500">{intelligence.versions?.length || 0} version(s)</span>
              {isGenerating && (
                <span className="flex items-center gap-1.5 text-xs text-yellow-400">
                  <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />Generating...
                </span>
              )}
            </div>
            {isGenerating && (
              <p className="text-sm text-slate-400">{String(intelContent?.message || 'Researching your market with AI...')}<span className="text-slate-500 ml-2">This typically takes 30-60 seconds.</span></p>
            )}
            {isError && (
              <p className="text-sm text-red-400">Generation failed: {String(intelContent?.error || 'Unknown error')}. <Link to={`/workspaces/${workspaceId}/intelligence`} className="text-helm-400 hover:text-helm-300 ml-1">View details</Link></p>
            )}
            {hasRealContent && (
              <p className="text-sm text-slate-400">{intelContent?.market_overview?.summary?.substring(0, 200)}... <Link to={`/workspaces/${workspaceId}/intelligence`} className="text-helm-400 hover:text-helm-300 ml-1">Read full report</Link></p>
            )}
            {!isGenerating && !isError && !hasRealContent && (
              <p className="text-sm text-slate-400">Intelligence report is available. <Link to={`/workspaces/${workspaceId}/intelligence`} className="text-helm-400 hover:text-helm-300 ml-1">View report</Link></p>
            )}
          </div>
        ) : (
          <div>
            {!hasBusinessContext && isAdmin ? (
              <p className="text-sm text-slate-400">Complete your business context first to generate a customized intelligence report.</p>
            ) : (
              <p className="text-sm text-slate-400">No intelligence report yet. Generate one to start the research process.</p>
            )}
          </div>
        )}
      </div>

      {/* Content Pillars Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Content Pillars</h2>
          {isAdmin && (
            <button onClick={() => setShowAddPillar(!showAddPillar)} disabled={!hasIntelligence}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${hasIntelligence ? 'bg-helm-500 hover:bg-helm-400 text-white' : 'bg-navy-800 text-slate-500 cursor-not-allowed border border-navy-600'}`}
              title={!hasIntelligence ? 'Generate an intelligence report first' : ''}>
              + Add Pillar
            </button>
          )}
        </div>

        {!hasIntelligence && pillars.length === 0 && (
          <div className="bg-navy-900/50 border border-navy-700 border-dashed rounded-xl p-8 text-center">
            <p className="text-slate-400 text-sm mb-2">Generate an intelligence report first, then add content pillars.</p>
          </div>
        )}

        {showAddPillar && (
          <div className="bg-navy-900 border border-helm-500/30 rounded-xl p-6 mb-4">
            <h3 className="text-white font-medium mb-2">Describe your content pillar</h3>
            <p className="text-xs text-slate-400 mb-4">Describe what this pillar should cover. The AI will use the intelligence report to research and structure it.</p>
            <textarea value={pillarPrompt} onChange={(e) => setPillarPrompt(e.target.value)}
              placeholder="e.g., I want a content pillar focused on roof repair and maintenance..." rows={4}
              className="w-full px-4 py-3 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500 text-sm" />
            <div className="flex items-center gap-3 mt-3">
              <button onClick={handleAddPillar} disabled={actionLoading === 'pillar' || !pillarPrompt.trim()} className="px-5 py-2 bg-helm-500 hover:bg-helm-400 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50">
                {actionLoading === 'pillar' ? 'Creating...' : 'Create Pillar'}
              </button>
              <button onClick={() => { setShowAddPillar(false); setPillarPrompt(''); }} className="px-4 py-2 bg-navy-800 hover:bg-navy-700 text-slate-300 text-sm rounded-lg border border-navy-600 transition-colors">Cancel</button>
            </div>
          </div>
        )}

        {pillars.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {pillars.map((pillar) => {
              const pillarArticles = articles.filter((a) => a.pillar_id === pillar.id);
              return (
                <Link key={pillar.id} to={`/workspaces/${workspaceId}/pillars/${pillar.id}`} className="bg-navy-900 border border-navy-700 rounded-xl p-5 hover:border-helm-500/30 transition-colors block">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-white font-medium text-sm leading-tight">{pillar.name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs ml-2 shrink-0 ${pillar.lifecycle_status === 'active' ? 'bg-green-500/20 text-green-400' : pillar.lifecycle_status === 'paused' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-500/20 text-slate-400'}`}>
                      {LIFECYCLE_STATUS_LABELS[pillar.lifecycle_status] || pillar.lifecycle_status}
                    </span>
                  </div>
                  {pillar.description && <p className="text-xs text-slate-400 mb-3 line-clamp-2">{pillar.description}</p>}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">{pillarArticles.length} article{pillarArticles.length !== 1 ? 's' : ''}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${BADGE_COLORS[pillar.research_status] || ''}`}>
                      {RESEARCH_STATUS_LABELS[pillar.research_status] || pillar.research_status}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Article Queue Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Article Queue</h2>
          {articles.length > 0 && (
            <Link to={`/workspaces/${workspaceId}/articles`} className="px-4 py-2 bg-helm-500 hover:bg-helm-400 text-white text-sm rounded-lg transition-colors">
              Open Full Queue
            </Link>
          )}
        </div>

        {articles.length === 0 && (
          <div className="bg-navy-900/50 border border-navy-700 border-dashed rounded-xl p-8 text-center">
            <p className="text-slate-400 text-sm">{pillars.length === 0 ? 'Add content pillars first, then articles will be auto-generated when pillars are locked.' : 'Lock your pillar research to auto-generate article topics.'}</p>
          </div>
        )}

        {articles.length > 0 && (
          <div className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy-700">
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Title</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Pillar</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Keyword</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => {
                  const pillar = pillars.find(p => p.id === article.pillar_id);
                  return (
                    <tr key={article.id} className="border-b border-navy-700/50 hover:bg-navy-800/50 transition-colors">
                      <td className="px-5 py-3">
                        {['draft', 'in_review', 'revision_requested', 'approved', 'scheduled', 'publishing', 'published', 'publish_failed'].includes(article.status) ? (
                          <Link to={`/workspaces/${workspaceId}/articles/${article.id}`} className="text-white hover:text-helm-400 text-sm font-medium transition-colors">{article.title}</Link>
                        ) : (
                          <span className="text-white text-sm font-medium">{article.title}</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {pillar ? (
                          <Link to={`/workspaces/${workspaceId}/pillars/${pillar.id}`} className="text-helm-400 hover:text-helm-300 text-sm transition-colors">{pillar.name}</Link>
                        ) : <span className="text-slate-400 text-sm">—</span>}
                      </td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[article.status] || 'bg-slate-500'}`} />
                          <span className="text-sm text-slate-300">{STATUS_LABELS[article.status] || article.status}</span>
                        </span>
                      </td>
                      <td className="px-5 py-3"><span className="text-slate-400 text-sm">{article.target_keyword || '—'}</span></td>
                      <td className="px-5 py-3"><span className="text-slate-500 text-xs">{new Date(article.created_at).toLocaleDateString()}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
